import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { Briefcase, FileText, Bookmark, MessageSquare, ArrowRight, TrendingUp } from 'lucide-react';

export default function ResearcherDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOpportunities, setRecentOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, oppsRes] = await Promise.all([
          api.get('/stats/dashboard'),
          api.get('/opportunities?limit=5')
        ]);
        setStats(statsRes.data);
        setRecentOpportunities(oppsRes.data.opportunities || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  const statCards = [
    { label: 'Messages', value: stats?.unread_messages || 0, icon: MessageSquare, color: '#0EA5E9', link: '/researcher/messages' },
    { label: 'Total Applications', value: stats?.total_applications || 0, icon: FileText, color: '#FF6B35' },
    { label: 'Pending', value: stats?.pending_applications || 0, icon: TrendingUp, color: '#F59E0B' },
    { label: 'Accepted', value: stats?.accepted_applications || 0, icon: Briefcase, color: '#10B981' },
    { label: 'Saved Opportunities', value: stats?.saved_opportunities || 0, icon: Bookmark, color: '#6366F1' },
  ];
  
  return (
    <DashboardLayout title={`Welcome back, ${user?.name?.split(' ')[0]}!`}>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, i) => {
          const Card = stat.link ? Link : 'div';
          const cardProps = stat.link ? { to: stat.link } : {};
          return (
            <Card 
              key={i}
              {...cardProps}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`bg-white p-6 border border-neutral-200 hover-lift ${stat.link ? 'cursor-pointer' : ''}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div 
                  className="w-10 h-10 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}15` }}
                >
                  <stat.icon size={20} style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
              <p className="text-sm text-neutral-500">{stat.label}</p>
            </Card>
          );
        })}
      </div>
      
      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Link
          to="/researcher/profile"
          data-testid="quick-action-profile"
          className="bg-[#FFF0EB] p-6 border border-[#FFD3C4] hover:border-[#FF6B35] transition-colors group"
        >
          <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit' }}>
            Complete Your Profile
          </h3>
          <p className="text-neutral-600 text-sm mb-4">
            Add your skills, certifications, and portfolio to stand out to clients.
          </p>
          <span className="text-[#FF6B35] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            Update Profile <ArrowRight size={16} />
          </span>
        </Link>
        
        <Link
          to="/researcher/opportunities"
          data-testid="quick-action-opportunities"
          className="bg-neutral-100 p-6 border border-neutral-200 hover:border-neutral-400 transition-colors group"
        >
          <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit' }}>
            Browse Opportunities
          </h3>
          <p className="text-neutral-600 text-sm mb-4">
            Discover new research projects that match your expertise.
          </p>
          <span className="text-neutral-800 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            View Opportunities <ArrowRight size={16} />
          </span>
        </Link>
      </div>
      
      {/* Recent Opportunities */}
      <div className="bg-white border border-neutral-200">
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>
            Recent Opportunities
          </h2>
          <Link 
            to="/researcher/opportunities"
            className="text-sm text-[#FF6B35] font-medium hover:underline"
          >
            View All
          </Link>
        </div>
        
        {recentOpportunities.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">
            No opportunities available yet.
          </div>
        ) : (
          <div className="divide-y divide-neutral-200">
            {recentOpportunities.map((opp) => (
              <Link
                key={opp.opportunity_id}
                to={`/opportunity/${opp.opportunity_id}`}
                data-testid={`opportunity-${opp.opportunity_id}`}
                className="block p-6 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-neutral-900 mb-1 truncate">
                      {opp.title}
                    </h3>
                    <p className="text-sm text-neutral-500 mb-2">
                      {opp.client?.name || 'Anonymous Client'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {opp.skills_needed?.slice(0, 3).map((skill, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    {opp.budget_max && (
                      <p className="font-semibold text-neutral-900">
                        ₹{opp.budget_min?.toLocaleString()} - ₹{opp.budget_max?.toLocaleString()}
                      </p>
                    )}
                    <p className="text-xs text-neutral-500 mt-1">
                      {opp.location_type}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      
      {/* Messages indicator */}
      {stats?.unread_messages > 0 && (
        <Link
          to="/researcher/messages"
          data-testid="unread-messages-banner"
          className="mt-6 bg-[#FF6B35] text-white p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <MessageSquare size={20} />
            <span className="font-medium">
              You have {stats.unread_messages} unread message{stats.unread_messages > 1 ? 's' : ''}
            </span>
          </div>
          <ArrowRight size={20} />
        </Link>
      )}
    </DashboardLayout>
  );
}
